import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { SafeDatePicker } from "components/SafeDatePicker";
import { Fetching } from "components/common/loading/fetching";
import { HotkeyProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { VideoControlProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { ProvideScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useLabellingLocations } from "hooks/useLabellingLocations";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useState } from "react";
import { useGetCommentedEventsQuery } from "src/store/api/generatedApi";
import { CommentReviewTable } from "./CommentReviewTable";

dayjs.extend(utc);

const DEFAULT_RANGE_DAYS = 7;

const CommentReviewContent = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();

  const [location, setLocation] = useState("");
  const [start, setStart] = useState<Dayjs>(() =>
    dayjs.utc().subtract(DEFAULT_RANGE_DAYS, "day").startOf("day")
  );
  const [end, setEnd] = useState<Dayjs>(() => dayjs.utc().endOf("day"));

  const { locationNames } = useLabellingLocations();

  const { data, isFetching, isError } = useGetCommentedEventsQuery(
    {
      tenantName,
      location,
      start: start.toISOString(),
      end: end.toISOString(),
    },
    { skip: !location }
  );

  const commentedEvents = data?.commented_events ?? [];

  return (
    <Stack spacing={3} sx={{ p: 3, flex: 1, overflow: "auto" }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Typography variant="h4" sx={{ flex: 1 }}>
          {t("commentReview")}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="comment-review-location-label" shrink>
            {t("location")}
          </InputLabel>
          <Select
            labelId="comment-review-location-label"
            value={location}
            label={t("location")}
            notched
            displayEmpty
            onChange={(e) => {
              setLocation(e.target.value);
            }}
          >
            <MenuItem value="" disabled>
              {t("pickALocation")}
            </MenuItem>
            {locationNames.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <SafeDatePicker
          label={t("startNoun")}
          value={start}
          onChange={(next) => {
            if (next?.isValid()) setStart(next.startOf("day"));
          }}
          slotProps={{ textField: { size: "small", sx: { minWidth: 150 } } }}
          format="YYYY-MM-DD"
        />
        <Typography variant="body2" color="text.secondary">
          –
        </Typography>
        <SafeDatePicker
          label={t("endNoun")}
          value={end}
          onChange={(next) => {
            if (next?.isValid()) setEnd(next.endOf("day"));
          }}
          slotProps={{ textField: { size: "small", sx: { minWidth: 150 } } }}
          format="YYYY-MM-DD"
        />
      </Stack>

      {!location ? (
        <Alert severity="info">{t("pickALocation")}</Alert>
      ) : isError ? (
        <Alert severity="error">{t("errorLoadingData")}</Alert>
      ) : (
        <Fetching fetching={isFetching} sx={{ minHeight: 200, flex: 1 }}>
          {commentedEvents.length === 0 ? (
            <Alert severity="info">{t("noCommentsInRange")}</Alert>
          ) : (
            <Box
              sx={{ flex: 1, minHeight: 400, minWidth: 0, overflowX: "hidden" }}
            >
              <CommentReviewTable
                commentedEvents={commentedEvents}
                location={location}
              />
            </Box>
          )}
        </Fetching>
      )}
    </Stack>
  );
};

export const CommentReviewPage = () => (
  <ProvideScrapEyeMetadata>
    <HotkeyProvider>
      <VideoControlProvider>
        <CommentReviewContent />
      </VideoControlProvider>
    </HotkeyProvider>
  </ProvideScrapEyeMetadata>
);
